/*
 * Portado do `enamed-feedback-lp`, com os mesmos testes: enunciado e
 * alternativas das questões da Aristo, que podem vir em HTML e trazer imagem
 * embutida em markdown.
 */

/** Elementos que abrem um parágrafo novo quando o texto vem em HTML. */
const BLOCK_TAGS = new Set([
  'P',
  'DIV',
  'LI',
  'UL',
  'OL',
  'TR',
  'TABLE',
  'H1',
  'H2',
  'H3',
  'H4',
  'H5',
  'H6',
  'BLOCKQUOTE',
]);

/**
 * Enunciados, alternativas e comentários vêm do banco da Aristo, e **podem vir
 * em HTML**. Este é o jeito seguro de mostrá-los sem trazer biblioteca nova:
 * extrai **só o texto**, respeitando parágrafos e quebras de linha.
 *
 * O `DOMParser` não executa script nem carrega imagem do documento que monta, e
 * nada do HTML chega ao DOM da página — o que sai daqui é string. O preço é
 * perder formatação rica (negrito, tabela, imagem). Se o conteúdo precisar
 * disso, o caminho é um sanitizador (DOMPurify), e é decisão de dependência.
 */
export function toParagraphs(value: string | null | undefined): string[] {
  if (!value) return [];

  const text = /<[a-z!/][\s\S]*>/i.test(value) ? htmlToText(value) : value;

  return text
    .split(/\n\s*\n/)
    .map((paragraph) => paragraph.replace(/[ \t]+/g, ' ').trim())
    .filter(Boolean);
}

function htmlToText(html: string): string {
  const doc = new DOMParser().parseFromString(html, 'text/html');

  const walk = (node: Node): string => {
    if (node.nodeType === Node.TEXT_NODE) return node.textContent ?? '';
    if (!(node instanceof Element)) return '';
    if (node.tagName === 'BR') return '\n';
    if (node.tagName === 'SCRIPT' || node.tagName === 'STYLE') return '';

    const inner = Array.from(node.childNodes).map(walk).join('');
    return BLOCK_TAGS.has(node.tagName) ? `\n\n${inner}\n\n` : inner;
  };

  return walk(doc.body);
}

/** Um pedaço do enunciado: texto corrido ou uma imagem. */
export type RichBlock =
  | { kind: 'text'; text: string }
  | { kind: 'image'; path: string; alt: string };

/** `![alt](caminho)` — o formato em que a Aristo embute imagem no enunciado. */
const MARKDOWN_IMAGE = /!\[([^\]]*)\]\(([^)\s]+)\)/g;

/**
 * O enunciado em blocos de texto e imagem, na ordem em que aparecem.
 *
 * **Existe porque produção embute imagem em markdown** —
 * `![](images/questions/<id>_body.png)` — dentro de um texto que de resto é
 * simples. O `toParagraphs` não conhece markdown, e o card mostrava o caminho
 * cru no meio do enunciado. Em geral são tabelas, eletrocardiogramas e
 * radiografias: sem elas a questão não tem como ser respondida.
 *
 * A imagem sai do parágrafo como bloco próprio, porque na prova ela ocupa uma
 * linha inteira; o texto antes e depois continua sendo parágrafo. Quem decide
 * **de onde** o caminho é servido é `resolveAristoImageUrl`, não este parser.
 */
export function toRichBlocks(value: string | null | undefined): RichBlock[] {
  const blocks: RichBlock[] = [];

  for (const paragraph of toParagraphs(value)) {
    let cursor = 0;
    for (const match of paragraph.matchAll(MARKDOWN_IMAGE)) {
      const before = paragraph.slice(cursor, match.index).trim();
      if (before) blocks.push({ kind: 'text', text: before });
      blocks.push({ kind: 'image', alt: match[1].trim(), path: match[2] });
      cursor = (match.index ?? 0) + match[0].length;
    }
    const rest = paragraph.slice(cursor).trim();
    if (rest) blocks.push({ kind: 'text', text: rest });
  }

  return blocks;
}
